import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Stopwatch, { StopwatchRef } from './Stopwatch';
import { putData } from './utils/PutData';
import { formatTime } from '../hooks/useSolve';
import { Alg } from 'cubing/alg';
import { CubeCtx } from './BluetoothCubeProvider';
import { StackmatState } from './services/ICubingService';


export interface BluetoothCubeComponentProps {
    streamid: string,
    alg: Alg,
    onComplete: (time: number) => void
}

const MemoizedStopwatch = React.memo(Stopwatch);

export const BluetoothCubeComponent: React.FC<BluetoothCubeComponentProps> = ({ streamid, alg, onComplete }) => {
    const stopwatchRef = useRef<StopwatchRef>(null);

    const { selectService, getService, serviceType } = useContext(CubeCtx);
    const [state, setState] = useState<StackmatState | null>(null);
    const [isRunning, setRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const service = getService(); // now kept inside the component function

    useEffect(() => {
        if (!service) return;

        let cancelled = false;

        const run = async () => {
            const isValid = await service.validateFacelets(alg);

            if (cancelled) return;

            if (isValid) {
                service.setReady(alg);
                setState(service.getCurrentState());
            } else {
                alert("Cube state does not match scramble. Please reset or inspect.");
            }

            service.setCallback((newState: React.SetStateAction<StackmatState | null>) => {
                if (!cancelled) setState(newState);
            });
        };

        run();

        return () => {
            cancelled = true;
            service.setCallback(() => { });
            setRunning(false);
        };
    }, [serviceType, service, alg]);

    useEffect(() => {
        if (state === null || state === undefined) return;
        if (!state.running) {
            if (stopwatchRef.current !== null) {
                if (!stopwatchRef.current.isStopped() && !state.running) {
                    stopwatchRef.current.stop();
                    stopwatchRef.current.setElapsedTime(state.timeMilli);
                    //console.log(e);
                    console.log("STOPPED : " + state.timeMilli);
                    putData(`https://datastream.singular.live/datastreams/${streamid}`, { time: formatTime(state.timeMilli), official: true }).then(() => {
                        onComplete(state.timeMilli);
                    });
                    //setTime(packet.timeInMilliseconds);

                }
                else {
                    if (stopwatchRef.current.getElapsedRunningTime() !== state.timeMilli) {
                        stopwatchRef.current.setElapsedTime(state.timeMilli)
                    }
                }
            }
        } else {
            if (stopwatchRef.current !== null) {
                if (stopwatchRef.current.isStopped() && state.running) {
                    //console.log("STARTED : " + state.timeMilli);
                    putData(`https://datastream.singular.live/datastreams/${streamid}`, { time: formatTime(state.timeMilli), official: false })
                    stopwatchRef.current.start();
                } else {
                    //console.log("RUNNING : " + state.timeMilli);
                    //putData(`https://datastream.singular.live/datastreams/${streamid}`, { time: formatTime(state.timeMilli), official: false })
                    stopwatchRef.current.setElapsedTime(state.timeMilli)
                }
            }
        }

    }, [state])

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
                bgcolor: "white", // White background
                opacity: .9,
                flexDirection: "column",
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
            }}
        >
            <>
                {!!state && state.on && <MemoizedStopwatch ref={stopwatchRef} streamid={streamid} />}
            </>
        </Box>
    );
};

